import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Flex } from '@chakra-ui/react';
import { useIsMutating } from '@tanstack/react-query';
import AnalysisHeader from './components/AnalysisHeader';
import AnalysisContent, { AnalysisContentHandles } from './components/AnalysisContent';
import { useAuth } from '../../context/AuthContext';
import AleaLoader from '../../components/AleaLoader/AleaLoader';
import {
  useAnalysisCareHomesQuery,
  useAnalysisResidentsQuery,
  useAnalysisStationsQuery,
} from './analysisPage.queries.ts';
import {
  URINATION_PATTERN_EXPORT_MUTATION_KEY,
  REPOSITIONING_HISTORY_EXPORT_MUTATION_KEY,
  URINE_HISTORY_EXPORT_MUTATION_KEY,
} from './analysisPage.mutations.ts';

// Improved FilterType definition
export type FilterType = 'facility' | 'period' | 'resident' | 'download' | null;

interface FilterState {
  facilityId?: string;
  facilityName?: string;
  startDate?: string;
  endDate?: string;
  stationIds?: string;
  residentIds?: string[];
  stationNames?: Record<string, string>;
  timestamp?: number;
}

// Type definitions
interface FilterData {
  facilityId?: string;
  startDate?: string;
  endDate?: string;
  stationIds?: string;
  residentIds?: string[];
  stationNames?: Record<string, string>;
}

const AnalysisPage: React.FC = () => {
  const { user } = useAuth();
  
  // Format date to DD.MM.YYYY
  const formatDate = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Calculate dates
  const today = new Date();
  const defaultStartDate = new Date();
  defaultStartDate.setDate(today.getDate() - 7);
  
  const formattedStartDate = formatDate(defaultStartDate);
  const formattedEndDate = formatDate(today);

  const [activeFilter, setActiveFilter] = useState<FilterType>(null);
  const [isChartLoading, setIsChartLoading] = useState<boolean>(false);
  const [isApplyingFilter, setIsApplyingFilter] = useState<boolean>(false);
  const [filterState, setFilterState] = useState<FilterState>({
    startDate: formattedStartDate,
    endDate: formattedEndDate,
    timestamp: Date.now()
  });
  
  // Create a reference to the AnalysisContent component
  const analysisContentRef = useRef<AnalysisContentHandles>(null);

  const careHomesQuery = useAnalysisCareHomesQuery();

  const facilities = useMemo(
    () => careHomesQuery.data ?? [],
    [careHomesQuery.data]
  );

  useEffect(() => {
    if (filterState.facilityId) return;

    const userCareHomeId = user?.carehome?.id;
    const userCareHomeName = user?.carehome?.name;

    if (userCareHomeId) {
      setFilterState(prev => ({
        ...prev,
        facilityId: userCareHomeId,
        facilityName: userCareHomeName,
        stationIds: undefined,
        residentIds: undefined,
        stationNames: undefined,
        timestamp: Date.now(),
      }));
      return;
    }

    if (facilities.length > 0) {
      setFilterState(prev => ({
        ...prev,
        facilityId: facilities[0].id,
        facilityName: facilities[0].name,
        stationIds: undefined,
        residentIds: undefined,
        stationNames: undefined,
        timestamp: Date.now(),
      }));
    }
  }, [
    facilities,
    filterState.facilityId,
    user?.carehome?.id,
    user?.carehome?.name,
  ]);

  const stationsQuery = useAnalysisStationsQuery(filterState.facilityId);

  const stationIdsParam = useMemo(
    () => (stationsQuery.data ?? []).map((station) => station.id).join(','),
    [stationsQuery.data]
  );

  const stationNamesParam = useMemo(() => {
    const names: Record<string, string> = {};
    for (const station of stationsQuery.data ?? []) {
      names[station.id] = station.name;
    }
    return names;
  }, [stationsQuery.data]);

  const residentsQuery = useAnalysisResidentsQuery(
    filterState.facilityId,
    stationIdsParam,
    stationsQuery.isFetched
  );

  const residentIdsParam = useMemo(
    () => (residentsQuery.data ?? []).map((resident) => resident.id),
    [residentsQuery.data]
  );

  useEffect(() => {
    if (!filterState.facilityId || filterState.facilityId === '1') return;
    if (filterState.stationIds !== undefined && filterState.residentIds !== undefined) {
      return;
    }
    if (!stationsQuery.isFetched || !residentsQuery.isFetched) return;

    setFilterState(prev => ({
      ...prev,
      stationIds: stationIdsParam,
      residentIds: residentIdsParam,
      stationNames: stationNamesParam,
      timestamp: Date.now(),
    }));
  }, [
    filterState.facilityId,
    filterState.residentIds,
    filterState.stationIds,
    residentIdsParam,
    residentsQuery.isFetched,
    stationIdsParam,
    stationNamesParam,
    stationsQuery.isFetched,
  ]);

  const handleFilterClick = (filterType: FilterType) => {
    if (activeFilter === filterType) {
      setActiveFilter(null);
    } else {
      setActiveFilter(filterType);
    }
  };

  const handleFilterApply = (filterType: FilterType, data: FilterData) => {
    // Special case for download option
    if (filterType === 'download') {
      if (data && 'exportType' in data && data.exportType === 'pdf') {
        // Call the PDF export function on the AnalysisContent component
        if (analysisContentRef.current) {
          analysisContentRef.current.exportToPdf();
        }
      }
      setActiveFilter(null); // Close the dropdown
      return;
    }

    setIsApplyingFilter(true);
    window.setTimeout(() => setIsApplyingFilter(false), 300);

    if (filterType === 'facility' && data?.facilityId) {
      const selectedFacility = facilities.find(
        (facility) => facility.id === data.facilityId
      );

      setFilterState(prev => ({
        ...prev,
        facilityId: data.facilityId,
        facilityName: selectedFacility?.name,
        stationIds: undefined,
        residentIds: undefined,
        stationNames: undefined,
        timestamp: Date.now(),
      }));
    } else if (filterType === 'period' && data?.startDate && data?.endDate) {
      setFilterState(prev => ({
        ...prev,
        startDate: data.startDate,
        endDate: data.endDate,
        timestamp: Date.now(),
      }));
    } else if (filterType === 'resident') {
      const hasResidentChanges = !areArraysEqual(
        data.residentIds || [],
        filterState.residentIds || []
      );

      if (
        hasResidentChanges ||
        data.stationIds !== filterState.stationIds ||
        data.stationNames !== filterState.stationNames
      ) {
        setFilterState(prev => ({
          ...prev,
          stationIds: data.stationIds,
          residentIds: data.residentIds,
          stationNames: data.stationNames,
          timestamp: Date.now(),
        }));
      }
    }

    setActiveFilter(null);
  };

  // Helper function to compare arrays
  const areArraysEqual = <T,>(a: T[] = [], b: T[] = []): boolean => {
    if (a.length !== b.length) return false;
    return a.every((val, idx) => val === b[idx]);
  };

  // Handle loading state from chart data
  const handleChartLoadingChange = (isLoading: boolean) => {
    setIsChartLoading(isLoading);
  };

  const isUrinationExportInFlight =
    useIsMutating({ mutationKey: URINATION_PATTERN_EXPORT_MUTATION_KEY }) > 0;
  const isRepositioningExportInFlight =
    useIsMutating({ mutationKey: REPOSITIONING_HISTORY_EXPORT_MUTATION_KEY }) > 0;
  const isUrineHistoryExportInFlight =
    useIsMutating({ mutationKey: URINE_HISTORY_EXPORT_MUTATION_KEY }) > 0;

  // Show loader when filter is applied or charts are loading
  const showLoader =
    isApplyingFilter ||
    careHomesQuery.isFetching ||
    stationsQuery.isFetching ||
    residentsQuery.isFetching ||
    isChartLoading ||
    isUrinationExportInFlight ||
    isRepositioningExportInFlight ||
    isUrineHistoryExportInFlight;

  return (
    <Flex flexFlow="column" borderRadius="20px" data-testid="analysis-page">
      {showLoader && <AleaLoader />}
      <AnalysisHeader 
        activeFilter={activeFilter}
        onFilterClick={handleFilterClick}
        onFilterApply={handleFilterApply}
        initialFilterState={filterState}
        facilities={facilities}
      />
      <AnalysisContent 
        ref={analysisContentRef}
        filterState={filterState} 
        onLoadingChange={handleChartLoadingChange}
      />
    </Flex>
  );
};

export default AnalysisPage;
